import { Piece } from "./Piece";
import { PositionMarker } from "./PositionMarker";
import { GameVars } from "../GameVars";
import { GameConstants } from "../GameConstants";

export class Board extends Phaser.Group {

    public static BOARD_PY: number = 558;

    public pieces: Piece[];

    private markers: PositionMarker[][];
    private backgroundImage: Phaser.Image;
    private colLabels: Phaser.Text[];
    private rowLabels: Phaser.Text[];
    private rotatingItemsContainer: Phaser.Group;
    private capturedPieceEffect: Phaser.Sprite;

    constructor(game: Phaser.Game) {

        super(game, null, "board");

        this.scale.y = GameVars.scaleY;

        this.x = GameConstants.GAME_WIDTH / 2;
        this.y = Board.BOARD_PY;

        this.backgroundImage = new Phaser.Image(this.game, 0, 0, "texture_atlas_1", "board_void.png");
        this.backgroundImage.anchor.set(.5);
        this.add(this.backgroundImage);

        this.rotatingItemsContainer = new Phaser.Group(this.game);
        this.add(this.rotatingItemsContainer);

        this.addPositionMarkers();
        this.addPieces();
        this.addCoordinates();

        this.capturedPieceEffect = new Phaser.Sprite(this.game, 0, 0, "texture_atlas_1", "ray_01.png");
        this.capturedPieceEffect.anchor.set(.5);
        this.capturedPieceEffect.animations.add("glow", Phaser.Animation.generateFrameNames("ray_", 1, 9, ".png", 2));
        this.capturedPieceEffect.visible = false;
        this.rotatingItemsContainer.add(this.capturedPieceEffect);
        
        this.setColorPlayer(GameVars.colorPlayer);
    }

    public setColorPlayer(colorPlayer: string): void {

        let boardAngle = colorPlayer === GameConstants.BLACK ? 180 : 0;

        this.rotatingItemsContainer.angle = boardAngle;

        for (let i = 0, ln = this.pieces.length; i < ln; i++) {
            
            this.pieces[i].angle = boardAngle;

            if (this.pieces[i].color === colorPlayer) {
                this.pieces[i].enableClick();
            } else {
                // puede ser que se haya ido cambiando de blanco a negro en
                // en el menu de seleccion de color de una partida contra AI
                this.pieces[i].disableClick();
            }
        }

        this.reorderCoordinates(boardAngle);
        this.capturedPieceEffect.angle = boardAngle;
    }

    public clearMarkers(): void {

        for (let c = 0; c < 8; c++) {
            for (let r = 0; r < 8; r++) {
                let marker: PositionMarker = this.markers[c][r];
                marker.visible = false;
            }
        }
    }

    public showMarkers(validPositions: string[]): void {

        for (let c = 0; c < 8; c++) {
            for (let r = 0; r < 8; r++) {
                // coger los 2 ultimos caracteres de valid position
                let marker: PositionMarker = this.markers[c][r];

                if (validPositions.indexOf(marker.p) > -1) {
                    marker.visible = true;
                }
            }
        }
    }

    public moveSelectedPiece(move: Move): void {

        let piece: Piece = GameVars.selectedPiece;

        // ponerla por encima de todo
        this.bringToTop(piece);
        piece.setPosition(move.to);

        // contemplar el caso de una promocion de la AI
        if (move.promotion) {
            this.game.time.events.add(750, function(): void {
                piece.promoteTo(move.promotion);
            }, this);
        }

        // contemplar el caso del enroque
        if (move.san === GameConstants.CASTLING_QUEEN || move.san === GameConstants.CASTLING_KING ) {

            let castlingData: {rook: Piece, p: string} = this.getCastlingRook(piece.color, move.san);

            this.game.time.events.add(400, function(): void {
                this.bringToTop(castlingData.rook);
                castlingData.rook.setPosition(castlingData.p, null, null, true);
            }, this, [castlingData]);
        }
    }

    public revertLastMovement(move: Move): void {

        let piece = GameVars.selectedPiece;
        piece.setPosition(move.from);

        if (GameVars.capturedPiece) {
            // se instancia una pieza
            const piece = new Piece(this.game, GameVars.capturedPiece.pieceType, this);   
            piece.setPosition(move.to, true);
            let boardAngle = GameVars.colorPlayer === GameConstants.BLACK ? 180 : 0;
            piece.angle = boardAngle;
            this.rotatingItemsContainer.add(piece);
            this.pieces.push(piece);
            piece.flash();
        }
    }

    public removePiece(piece: Piece): void {

        const i = this.pieces.indexOf(piece);
        this.pieces.splice(i, 1);

        // mostrar el efecto de captura
        this.capturedPieceEffect.position = piece.position;

        this.capturedPieceEffect.visible = true;
        this.rotatingItemsContainer.bringToTop(this.capturedPieceEffect);

        this.capturedPieceEffect.play("glow", 15, false)
            .onComplete.add(function(): void {
                this.capturedPieceEffect.visible = false;
            }, this);

        piece.kill(true);
    }

    public check(uploadedGame?: boolean): void {

        // poner al rey del turno actual en jaque
        for (let i: number = 0; i < this.pieces.length; i ++) {
            
            let piece: Piece = this.pieces[i];

            if (GameVars.challenge) {
                if ((piece.pieceType === GameConstants.BLACK_KING && GameVars.currentTurn === GameConstants.WHITE) ||
                (piece.pieceType === GameConstants.WHITE_KING && GameVars.currentTurn === GameConstants.BLACK) 
                ) {
                    piece.check();
                    break;
                }
            } else {
                if ((piece.pieceType === GameConstants.BLACK_KING && GameVars.currentTurn === GameConstants.BLACK) ||
                (piece.pieceType === GameConstants.WHITE_KING && GameVars.currentTurn === GameConstants.WHITE) 
                ) {
                    piece.check();
                    break;
                }
            }
        }
    }

    public uncheck(): void {

        // sacar al rey del turno actual del jaque
        for (let i: number = 0; i < this.pieces.length; i ++) {
            
            let piece: Piece = this.pieces[i];

            if (piece.inCheck) {
                piece.uncheck();
                break;
            }
        }
    }

    public getPieceAt(p: string): Piece {

        let piece: Piece = null;

        for (let i = 0, ln = this.pieces.length; i < ln; i++) {

            if (this.pieces[i].p === p) {
                piece = this.pieces[i];
                break;
            }
        }

        return piece;
    }

    public newTurn(): void {

        // habilitar / deshabilitar las piezas segun el turno
        for (let i: number = 0, ln: number = this.pieces.length; i < ln; i++) {

            if (this.pieces[i].color === GameVars.currentTurn) {
                this.pieces[i].enableClick();
            } else {
                this.pieces[i].disableClick();
            }
        }
    }

    private addPositionMarkers(): void {

        this.markers = [];

        for (let i: number = 0; i < 8; i++) {

            this.markers[i] = [];

            for (let j: number = 0; j < 8; j++) {
                let marker: PositionMarker = new PositionMarker(this.game, this);
                marker.x = GameConstants.SQUARE_WIDTH * (i - 3.5);
                marker.y = GameConstants.SQUARE_WIDTH * (j - 3.5);
                marker.p = GameConstants.COLUMNS[i] + GameConstants.ROWS[j];
                marker.visible = false;
                this.rotatingItemsContainer.add(marker);
                this.markers[i].push(marker);
            }
        }
    }

    private addPieces(): void {

        this.pieces = [];

        // dependiendo del FEN instanciar y situar las correspondientes piezas
        let fen: string = GameVars.chess.fen();

        let rows: string[] = fen.split("/");
        let tmp: string[] = rows[7].split(" ");
        rows[7] = tmp[0];

        let col: number;
        let piece: Piece;

        for (let row = 0; row < 8; row++) {

            col = 0;

            for (let j = 0; j < rows[row].length; j++) {

                // es un numero?
                let n = parseInt(rows[row][j]);

                if (!isNaN(n)) {
                    col += n;
                } else {

                    let pieceType: string = rows[row][j];

                    // se instancia una pieza
                    piece = new Piece(this.game, pieceType, this);
                    let column: string = GameConstants.COLUMNS[col];
                    piece.setPosition(column + (8 - row), true);

                    this.rotatingItemsContainer.add(piece);

                    this.pieces.push(piece);

                    col++;
                }
            }
        }
    }

    private addCoordinates(): void {

        this.colLabels = [];
        this.rowLabels = [];

        let label: Phaser.Text;

        for (let i = 0; i < 8; i++) {

            label = new Phaser.Text(this.game, -372, GameConstants.SQUARE_WIDTH * (i - 3.5), GameConstants.ROWS[i], { font: "22px Roboto Light", fill: "#FFFFFF", align: "center"});
            label.anchor.set(.5);
            label.alpha = .65;
            this.add(label);

            this.rowLabels.push(label);

            // las columnas
            label = new Phaser.Text(this.game, GameConstants.SQUARE_WIDTH * (i - 3.5), 374, GameConstants.COLUMNS[i].toUpperCase(), { font: "20px Roboto Slab Light", fill: "#FFFFFF", align: "center"});
            label.anchor.set(.5);
            label.alpha = .75;
            this.add(label);

            this.colLabels.push(label);
        }
    }

    private getCastlingRook(castlingKingColor: string, san: string): {rook: Piece, p: string} {

        let rook: Piece;
        let p: string;

        if (castlingKingColor === GameConstants.BLACK) {

            if (san === GameConstants.CASTLING_KING) {
                rook = this.getPieceAt("h8");
                p = "f8";
            } else {
                rook = this.getPieceAt("a8");
                p = "d8";
            }
        } else {

            if (san === GameConstants.CASTLING_KING) {
                rook = this.getPieceAt("h1");
                p = "f1";
            } else {
                rook = this.getPieceAt("a1");
                p = "d1";
            }
        }

        return {rook: rook, p: p};
    }

    private reorderCoordinates(boardAngle: number): void {

        if (boardAngle === 180) {
            for (let i = 0; i < 8; i++) {
                this.colLabels[i].text = GameConstants.COLUMNS[7 - i].toUpperCase();
                this.rowLabels[i].text = GameConstants.ROWS[7 - i];
            }
        } else {
            for (let i = 0; i < 8; i++) {
                this.colLabels[i].text = GameConstants.COLUMNS[i].toUpperCase();
                this.rowLabels[i].text = GameConstants.ROWS[i];
            }
        }
    }
}
